import { Component, computed, OnInit, signal, WritableSignal, inject } from '@angular/core';
import { ProfileDropdownComponent } from '../../../shared/components/profile-dropdown/profile-dropdown.component';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { FieldPathDirective, FormOptionsDirective } from '@bo/ng-forms';
import { AppFormComponent } from '../../../shared/classes/app-form';
import {
  EmployeeIdentityDocument,
  EmployeeIdentityDocumentRequest,
  EmployeeIdentityDocumentStatus,
  IdNumType,
} from '../../../_interfaces/employee.interface';
import { EmployeeApiService } from '../../../_services/employee-service/api/employee-api.service';
import { Method } from '@bo/ng-api';
import { Observable } from 'rxjs';
import { FieldComponent } from '../../../shared/components/forms/form-field/form-field.component';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { SubmitComponent } from '../../../shared/components/forms/submit/submit.component';
import { HttpErrorResponse } from '@angular/common/http';
import { RouterLink } from '@angular/router';
import { Store } from '@ngxs/store';
import { IdVerificationState } from '../../../store/id-verification/id-verification.state';
import {
  StartPollingIDVerificationStatus,
  StopPollingIDVerificationStatus,
} from '../../../store/id-verification/id-verification.actions';
import { UpdatedSideGraphic } from '../../../store/onboarding/onboarding.actions';
import { ProgressBarService } from '../../../_services/progress-bar-service/progress-bar.service';
import { UploadImgCropComponent } from '../../../shared/components/upload-img-crop/upload-img-crop.component';
import { AppSelectors } from '../../../store/app/app.selectors';
import { idStatus } from '../../../shared/utils/general-utils';
import { VerifyIdSelectOptions, verifyIdSelectOptions } from './utils';
import { helpCenterUrl } from '../../../_fixtures/project-fixtures';
import {
  BarcodeValidationResult,
  BarcodeValidationStatus,
} from '../../../shared/interfaces/barcode-validation.interface';

@Component({
  selector: 'app-verify-identity',
  imports: [
    ProfileDropdownComponent,
    NgClass,
    ReactiveFormsModule,
    FormOptionsDirective,
    FieldComponent,
    FieldPathDirective,
    LoaderComponent,
    SubmitComponent,
    RouterLink,
    UploadImgCropComponent,
    NgTemplateOutlet,
  ],
  templateUrl: './verify-identity.component.html',
})
export class VerifyIdentityComponent extends AppFormComponent<EmployeeIdentityDocument> implements OnInit {
  readonly employeeApiService = inject(EmployeeApiService);
  readonly store = inject(Store);
  readonly progressBarService = inject(ProgressBarService);
  readonly idVerificationStatus = this.store.select(IdVerificationState.IDVerificationStatus);
  readonly userProfile$ = this.store.select(AppSelectors.userDetails);

  minWidth = signal<number>(640);
  minHeight = signal<number>(320);

  protected readonly idStatus = idStatus;
  protected override fields: string[] = ['id_number'];
  readonly optionsURL = this.employeeApiService.URL_EMPLOYEE_IDENTITY_DOCUMENT;

  fileFrontUrl = signal<string>('');
  fileBackUrl = signal<string>('');
  placeholder = signal<string>('');
  userIdNumber = signal<string>('');
  userIdNumberType = signal<IdNumType | undefined>(undefined);
  readonly destination = 'employee-id-doc';

  // Barcode validation signals
  frontBarcodeValidation = signal<BarcodeValidationResult | null>(null);
  backBarcodeValidation = signal<BarcodeValidationResult | null>(null);
  frontValidationStatus = signal<BarcodeValidationStatus>(BarcodeValidationStatus.IDLE);
  backValidationStatus = signal<BarcodeValidationStatus>(BarcodeValidationStatus.IDLE);

  loadingData = true;
  readonly initialTitle = 'Get Verified';
  readonly initialInfoText = `This helps us check that you’re really you. <br>
     Identity verification is one of the ways we keep Paystashio secure.`;
  readonly helpCenterUrl = helpCenterUrl;
  idNumError: string | undefined;
  showSuccess = false;
  showTimedOut = false;
  title: string | undefined;
  infoText: string | undefined;
  fileUploadTitle: string | undefined;

  checkedSmartID = signal<boolean>(false);
  checkedGreenID = signal<boolean>(false);
  showOptionStep = signal<boolean>(false);
  showUploadStep = signal<boolean>(false);
  optionSelected = signal<boolean>(false);
  fieldValueSilentUpdate = signal<boolean>(false);

  noSubmissionYet = computed(() => this.idStatus(this.currentStatus()).isNull);
  submissionCreated = computed(() => this.idStatus(this.currentStatus()).isNew);
  submissionError = computed(() => this.idStatus(this.currentStatus()).isError);
  submissionRejected = computed(() => this.idStatus(this.currentStatus()).isRejected);
  submissionVerified = computed(() => this.idStatus(this.currentStatus()).isVerified);

  // Computed signals for barcode validation
  isFrontValidationInProgress = computed(() => this.frontValidationStatus() === BarcodeValidationStatus.SCANNING);
  isBackValidationInProgress = computed(() => this.backValidationStatus() === BarcodeValidationStatus.SCANNING);
  isAnyValidationInProgress = computed(() => this.isFrontValidationInProgress() || this.isBackValidationInProgress());

  // temporarily force checks to be true to enable user to bypass the scanner until its enabled again
  frontBarcodeValid = computed(() => true);
  backBarcodeValid = computed(() => true);

  submitDisabled = computed(() => {
    if (this.showOptionStep()) {
      return !this.optionSelected();
    } else if (this.showUploadStep()) {
      // Check if validation is in progress
      if (this.isAnyValidationInProgress()) {
        return true;
      }

      if (this.checkedSmartID()) {
        // Smart ID requires both front and back files, but only back file with valid barcode
        return !this.fileFrontUrl() || !this.fileBackUrl() || (this.fileBackUrl() && !this.backBarcodeValid());
      } else {
        // Passport/Green ID only requires front with a valid barcode
        return !this.fileFrontUrl() || !this.frontBarcodeValid();
      }
    } else {
      return false;
    }
  });

  currentStatus: WritableSignal<EmployeeIdentityDocumentStatus | null> = signal(null);
  timedOut: WritableSignal<boolean> = signal(false);

  selectOptions: VerifyIdSelectOptions[] = verifyIdSelectOptions;

  constructor() {
    super();
    this.store.dispatch(new UpdatedSideGraphic('verifyId.svg'));
  }

  override ngOnInit() {
    this.progressBarService.setHideProgress(true);
    this.store.dispatch(new StartPollingIDVerificationStatus());
    this.getExistingInfo();
    this.subscriptions.add(
      this.idVerificationStatus?.subscribe({
        next: (data) => {
          this.currentStatus.set(data.status);
          this.timedOut.set(data.timeout);
          if (this.noSubmissionYet()) {
            this.setInitialStep();
          }
          if (this.submissionCreated() || this.submissionError()) {
            this.setProcessingState();
          }
          if (this.timedOut()) {
            this.title = `Whoops ...`;
            this.infoText = `The process is taking longer than expected. <br>
                             We will keep trying to validate your documents. <br>
                             You can contact our support team for any further assistance.`;
            this.showTimedOut = true;
          }
          if (this.submissionVerified()) {
            this.setSuccessState();
          }

          if (this.submissionRejected()) {
            this.setRejectedState();
          }
        },
      }),
    );
    super.ngOnInit();
  }

  getSelectedOption(): VerifyIdSelectOptions | undefined {
    return this.selectOptions.find((option) => option.checked);
  }

  getExistingInfo() {
    this.subscriptions.add(
      this.userProfile$.subscribe({
        next: (data) => {
          if (data) {
            this.userIdNumber.set(data.idnum);
            this.userIdNumberType.set(data.idnum_type);
            if (this.userIdNumberType() === IdNumType.RSAID) this.onSelectOption('smartId');
            if (this.userIdNumberType() === IdNumType.Passport) this.onSelectOption('passport');
          }
        },
      }),
    );
  }

  setInitialStep(hasFrontUploaded?: boolean) {
    this.title = this.initialTitle;
    this.infoText = this.initialInfoText;
    this.store.dispatch(new UpdatedSideGraphic('verifyId.svg'));
    this.showOptionStep.set(true);
    this.showUploadStep.set(false);
    this.resetSteps(hasFrontUploaded);
  }

  setUploadStep() {
    this.title = this.getSelectedOption()?.label;
    this.infoText = this.getSelectedOption()?.infoText;
    this.fileUploadTitle = this.getSelectedOption()?.fileUploadTitle;
    this.showUploadStep.set(true);
    this.showOptionStep.set(false);
  }

  setRejectedState() {
    this.showOptionStep.set(false);
    this.showUploadStep.set(false);
    this.showSuccess = false;
    this.store.dispatch(new UpdatedSideGraphic('verifyId_rejected.svg'));
    const plural = this.checkedGreenID() || this.checkedSmartID() ? 's' : '';
    this.title = `Whoops! <br> Can't read document${plural}`;
    this.infoText = `We couldn't read your document${plural}. <br> Please contact our support team for further assistance`;
  }

  setSuccessState() {
    this.showOptionStep.set(false);
    this.showUploadStep.set(false);
    this.showSuccess = true;
    this.store.dispatch(new UpdatedSideGraphic('verifyId.svg'));
    this.title = `Success!`;
    this.infoText = `Thank you for taking the time to complete the identity verification process.`;
  }

  /**
   * Handle barcode validation result for front image
   */
  onFrontBarcodeValidationResult(result: BarcodeValidationResult): void {
    // If the image was removed, reset validation completely
    if (result.error?.code === 'INVALID_IMAGE' && result.error?.message === 'Image removed') {
      this.frontBarcodeValidation.set(null);
      return;
    }

    this.frontBarcodeValidation.set(result);
  }

  /**
   * Handle barcode validation status change for the front image
   */
  onFrontBarcodeValidationStatus(status: BarcodeValidationStatus): void {
    this.frontValidationStatus.set(status);

    // If status is reset to IDLE, and we have a validation result, clear it
    if (status === BarcodeValidationStatus.IDLE && this.frontBarcodeValidation()) {
      this.frontBarcodeValidation.set(null);
    }
  }

  /**
   * Handle barcode validation result for back image
   */
  onBackBarcodeValidationResult(result: BarcodeValidationResult): void {
    // If the image was removed, reset validation completely
    if (result.error?.code === 'INVALID_IMAGE' && result.error?.message === 'Image removed') {
      this.backBarcodeValidation.set(null);
      return;
    }
    this.backBarcodeValidation.set(result);
  }

  /**
   * Handle barcode validation status change for back image
   */
  onBackBarcodeValidationStatus(status: BarcodeValidationStatus): void {
    this.backValidationStatus.set(status);

    // If status is reset to IDLE, and we have a validation result, clear it
    if (status === BarcodeValidationStatus.IDLE && this.backBarcodeValidation()) {
      this.backBarcodeValidation.set(null);
    }
  }

  setProcessingState() {
    this.showUploadStep.set(false);
    this.showOptionStep.set(false);
    this.title = 'Processing ...';
    this.infoText = `Please wait for verification to complete. <br>
              This may take a few minutes.`;
  }

  resetSteps(hasFrontUploaded?: boolean) {
    if (!hasFrontUploaded) this.fileFrontUrl.set('');
    this.fileBackUrl.set('');
    this.showSuccess = false;
    this.store.dispatch(new UpdatedSideGraphic('verifyId.svg'));
  }

  onSelectOption(id: string) {
    this.checkedSmartID.set(id === 'smartId');
    this.checkedGreenID.set(id === 'greenId');
    this.selectOptions.forEach((option) => {
      option.checked = option.id === id;
    });
    this.minWidth.set(this.getSelectedOption()?.imageWidth ?? 600);
    this.minHeight.set(this.getSelectedOption()?.imageHeight ?? 400);
    this.placeholder.set(this.getSelectedOption()?.placeholder ?? 'Enter your identity number');
    this.optionSelected.set(true);
  }

  switchIdTo(id: string, hasFrontUploaded?: boolean) {
    this.setInitialStep(hasFrontUploaded);
    this.onSelectOption(id);
    this.setUploadStep();
  }

  fileFrontUploaded($event: string) {
    this.fileFrontUrl.set($event);
  }

  fileBackUploaded($event: string) {
    this.fileBackUrl.set($event);
  }

  override createForm(): void {
    const options$ = this.employeeApiService.getOptions(this.optionsURL, Method.POST, true);
    this.subscriptions.add(
      this.buildFromOptions(options$).subscribe({
        next: () => {
          this.loadingData = false;
          if (this.userIdNumber() && !this.noSubmissionYet()) {
            this.form.controls['id_number'].setValue(this.userIdNumber());
          }
          this.form.controls['id_number'].valueChanges.subscribe((value) => {
            this.idNumError = undefined;

            // 2. Clean up the value
            if (this.checkedSmartID() || this.checkedGreenID()) {
              if (typeof value === 'string' && value.includes(' ')) {
                const cleanedValue = value.replaceAll(/\s/g, '');

                // Set the cleaned value back to the control.
                // IMPORTANT: Use { emitEvent: false } to prevent this setValue call
                // from triggering valueChanges again, which would cause an infinite loop.
                this.form.controls['id_number'].setValue(cleanedValue, { emitEvent: false });
                this.fieldValueSilentUpdate.set(true);
              }

              if (this.fieldValueSilentUpdate()) {
                if (this.form.controls['id_number'].value.length > 13) {
                  this.idNumError = 'Ensure this field has no more than 13 characters.';
                }
              }
            }
          });
        },
      }),
    );
  }

  override submitData(formData: EmployeeIdentityDocumentRequest): Observable<EmployeeIdentityDocument> {
    const data: EmployeeIdentityDocumentRequest = {
      ...formData,
      file_front: this.fileFrontUrl(),
    };
    if (this.checkedSmartID()) {
      data.file_back = this.fileBackUrl();
    }
    return this.employeeApiService.postEmployeeIdentityDocument(data);
  }

  override handleSubmitSuccess(): void {
    this.store.dispatch(new StartPollingIDVerificationStatus());
  }

  protected override handleSubmitError(err: unknown): void {
    if (err instanceof HttpErrorResponse && err?.error.id_number) {
      this.form.get('id_number')?.markAsTouched();
      this.idNumError = `Your ${this.userIdNumberType() === IdNumType.RSAID ? 'identity' : 'passport'} number doesn't match what your admin provided. <br> Please contact your admin or retry.`;
    } else {
      this.setUploadStep();
    }
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.store.dispatch(new StopPollingIDVerificationStatus());
  }

  protected readonly Boolean = Boolean;
}
